import { ShopifyConnectedToast } from '@/components/integrations/ShopifyConnectedToast';
import { useAuth } from '@/contexts/auth-context';
import SubscriptionGuard from '@/guards/SubscriptionGuard';
import { SuccessFeeTermsLockGate } from '@/components/legal/success-fee-terms';
import { LoadingPage } from '@/components/ui/loading-page';
import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, refreshCurrentUser } = useAuth();
  const location = useLocation();
  const [hasAttemptedAuth, setHasAttemptedAuth] = useState(false);

  useEffect(() => {
    // If not authenticated and not loading, try to refresh the session once
    if (!isAuthenticated && !isLoading && !hasAttemptedAuth) {
      setHasAttemptedAuth(true);
      refreshCurrentUser({ force: false }).catch(() => {
        console.log('[ProtectedRoute] Failed to refresh session');
      });
    }
  }, [isAuthenticated, isLoading, hasAttemptedAuth, refreshCurrentUser]);

  // Show loading spinner while checking authentication
  if (isLoading) {
    return <LoadingPage />;
  }

  // If authenticated, check the success-fee terms lock before the subscription.
  // Order matters: while locked the API refuses the subscription call, and the
  // guard reads that refusal as an expired subscription and sends a paying
  // merchant to the pricing page.
  if (isAuthenticated) {
    return (
      <>
        <ShopifyConnectedToast />
        <SuccessFeeTermsLockGate>
          <SubscriptionGuard>{children}</SubscriptionGuard>
        </SuccessFeeTermsLockGate>
      </>
    );
  }

  // If not authenticated and we've attempted auth check, redirect to login
  if (hasAttemptedAuth) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Still checking auth
  return <LoadingPage />;
}
